import { Controller, useController, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ChatCircle, Phone, VideoCamera } from "@phosphor-icons/react"
import { Input } from "@/components/ui/input"
import { ToggleGroup, ToggleGroupItem, ToggleItem } from "@/components/ui/toggle-group"
import { isFlatLength } from "@/lib/appointment-type-rules"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import {
  isChatSelectable,
  isMedicalService,
} from "@/modules/patients/agenda/teleconsultation/booking/libs/teleconsultation-helper"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { PhoneNumberInput } from "@/modules/patients/components/phoneNumber/PhoneNumberInput.tsx"
import { CommunicationChannel, ContactChannel } from "@/types/__generated__/union-types"

type Channel = ToggleItem & {
  value: CommunicationChannel
}

const CommunicationChannelSelection = ({
  contactChannel,
  isEditing,
  isMedicalChatEnabled,
}: {
  contactChannel: ContactChannel
  isEditing: boolean
  isMedicalChatEnabled: boolean
}) => {
  const { t } = useTranslation()
  const {
    control,
    formState: { errors },
  } = useFormContext<TeleconsultationForm>()
  const { field: channel } = useController({ name: "channel", control })
  const serviceWatch = useWatch({ control, name: "service" })
  const appointmentTypeWatch = useWatch({ control, name: "appointmentType" })
  const addressSource = useWatch({ control, name: "addressSource" })
  const assignedPartner = useWatch({ control, name: "assignedPartner" })

  const isMedgateConnectService = isMedgateConnect(serviceWatch)
  const showPhoneNumber = channel.value === "PHONE" || (channel.value === "VIDEO" && contactChannel !== "APP")
  const isPartnerRecipient = isMedgateConnectService && addressSource === "PARTNER"
  const canSelectChat = isChatSelectable({ contactChannel, service: serviceWatch, isMedicalChatEnabled })
  // A flat-length appointment reserves live time with one agent, so it is conducted over a live channel only
  const chatChannel: Channel[] =
    isMedgateConnectService || isFlatLength(appointmentTypeWatch)
      ? []
      : [
          {
            value: "CHAT",
            icon: <ChatCircle />,
            disabled: !canSelectChat,
          },
        ]
  const showMedicalChatNotEnabledInfo =
    chatChannel.length > 0 && !isMedicalChatEnabled && isMedicalService(serviceWatch)

  const COMMUNICATION_CHANNELS: Channel[] = [
    {
      value: "PHONE",
      icon: <Phone />,
      disabled: false,
    },
    {
      value: "VIDEO",
      icon: <VideoCamera />,
      disabled: false,
    },
    ...chatChannel,
  ]

  return (
    <div className="grid gap-4">
      <ToggleGroup
        type="single"
        {...channel}
        onValueChange={(value) => value && channel.onChange(value)}
        className="h-11.5">
        {COMMUNICATION_CHANNELS.map(({ icon, ...item }) => (
          <ToggleGroupItem
            key={item.value}
            {...item}
            data-testid={`channel-${item.value}`}
            className="min-w-17 px-4"
            disabled={item.disabled}>
            {icon}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {isEditing && channel.value === "ASYNC_REQUEST" && (
        <div className="text-on-surface-subdued px-3 text-xs tracking-[0.25px]">
          {t("patient.agenda.communicationChannel.asyncAppointmentInfo")}
        </div>
      )}
      {showMedicalChatNotEnabledInfo && (
        <div
          className="text-on-surface-subdued px-3 text-xs tracking-[0.25px]"
          data-testid="medical-chat-not-enabled-info">
          {t("patient.agenda.communicationChannel.medicalChatNotEnabledInfo")}
        </div>
      )}

      {showPhoneNumber && (
        <Controller
          key={`${isPartnerRecipient ? "partner" : "patient"}-${assignedPartner}`}
          name="patientPhoneNumber"
          render={({ field: { value, name, onChange } }) => (
            <div id={name}>
              <PhoneNumberInput
                label={t(isPartnerRecipient ? "patient.agenda.partnerPhoneNumber" : "patient.agenda.phoneNumber")}
                value={value || ""}
                onChange={onChange}
                error={t(errors?.patientPhoneNumber?.message ?? "")}
                isSMSSupported
                isCorrespondence
                disabled={isPartnerRecipient}
              />
            </div>
          )}
          control={control}
        />
      )}
      {isPartnerRecipient && assignedPartner && (
        <Input
          label={t("patient.agenda.assignedPartner")}
          value={assignedPartner}
          disabled
          data-testid="partner-email"
        />
      )}
    </div>
  )
}

export { CommunicationChannelSelection }
